import React from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import Form, { ErrorMessage, Field } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import SearchIcon from '@atlaskit/icon/core/migration/search';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { type ValueType } from '@atlaskit/select';
import { getEscalations, getTeams, getUsers } from '@atlassian/eoc-auto-complete';
import { isRequired, useValidators } from '@atlassian/eoc-common';
import {
	CancelButton,
	EXTRA_SELECT_PROPS,
	commonMessages as scheduleCommonMessages,
} from '@atlassian/eoc-schedule-common';

import { messages as commonMessages } from '../../../../../../../../common/messages';
import { AutoCompleteWithNoOne } from '../../../../../../../../common/ui/auto-complete-with-noone';
import { type AutoCompleteWithNoOneItem } from '../../../../../../../../common/ui/auto-complete-with-noone/custom-option/types';

import { Container } from './styled';
import { type FormFields, type ReplaceWithProps } from './types';

const formStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	rowGap: 'space.300',
	marginTop: 'space.100',
});

const ReplaceWith = ({ ignored, onClose, onConfirm }: ReplaceWithProps) => {
	const intlApi = useIntl();

	const isRequiredValidator = useValidators([isRequired()]);

	return (
		<Container role="dialog" aria-labelledby="replace-with-title">
			<Heading id="replace-with-title" as="h2" size="medium">
				{intlApi.formatMessage(commonMessages.replaceWith)}
			</Heading>

			<Form<FormFields> onSubmit={({ responder }) => onConfirm(responder)}>
				{({ formProps }) => (
					<Box
						as="form"
						xcss={formStyles}
						{...formProps}
						onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
							e.stopPropagation();
							formProps.onSubmit(e);
						}}
					>
						<Field<ValueType<AutoCompleteWithNoOneItem, false>>
							name="responder"
							label={intlApi.formatMessage(commonMessages.participants)}
							validate={isRequiredValidator}
						>
							{({ fieldProps, error }) => (
								<>
									<AutoCompleteWithNoOne
										{...EXTRA_SELECT_PROPS}
										{...fieldProps}
										placeholder={intlApi.formatMessage(commonMessages.addParticipants)}
										// Do not show selected responder
										filterOption={({ value }) => (value === ignored.id ? false : true)}
										components={{
											// FIX: Custom Option doesn't render for some reason
											DropdownIndicator: () => (
												<SearchIcon
													color="currentColor"
													spacing="spacious"
													label={intlApi.formatMessage(commonMessages.search)}
												/>
											),
										}}
										endpoints={[getUsers, getTeams, getEscalations]}
										isClearable
									/>
									{error && <ErrorMessage>{error}</ErrorMessage>}
								</>
							)}
						</Field>

						<Flex alignItems="center" justifyContent="end" columnGap="space.050">
							<CancelButton onClick={() => onClose()}>
								{intlApi.formatMessage(scheduleCommonMessages.close)}
							</CancelButton>
							<Button appearance="primary" type="submit">
								{intlApi.formatMessage(commonMessages.confirm)}
							</Button>
						</Flex>
					</Box>
				)}
			</Form>
		</Container>
	);
};

export { ReplaceWith };
